// React Router
import { useNavigate } from 'react-router-dom';

// Mantine
import { SimpleGrid } from '@mantine/core';

// Custom Components
import { SubmissionCard } from './SubmissionCard';
import { RemittanceCard } from './RemittanceCard';
import { AgingClaimsCard } from './AgingClaimsCard';
import { ErrorClaimsCard } from './ErrorClaimsCard';

// Types
import { Status } from 'interfaces';
import { SiteMetricsResult } from '@billing/validation';

interface DashboardMetricsPanelProps {
  icons: Record<string, any>;
  metricData: Partial<SiteMetricsResult>;
  loading: boolean;
  statuses: Status[];
}

export const DashboardMetricsPanel: React.FC<DashboardMetricsPanelProps> = ({
  icons,
  metricData,
  statuses,
  loading,
}) => {
  const navigate = useNavigate();
  const latestSubmission = metricData?.latestSubmission
    ? {
        filename: metricData.latestSubmission.filename,
        submittedAt: metricData.latestSubmission.submittedAt,
        claimCount: metricData.latestSubmission.claimCount,
        status: metricData.latestSubmission.status,
        totalAmount: metricData.latestSubmission.totalAmount
          ? String(metricData.latestSubmission.totalAmount)
          : '—',
      }
    : null;

  const latestRemittance = metricData?.latestRemittance
    ? {
        filename: metricData.latestRemittance.filename,
        parsedAt: metricData.latestRemittance.parsedAt,
        totalFeeAssessed: metricData.latestRemittance.totalFeeAssessed
          ? String(metricData.latestRemittance.totalFeeAssessed)
          : '—',
        claimCount: metricData.latestRemittance.claimCount || 0,
      }
    : null;

  return (
    <SimpleGrid
      cols={4}
      spacing="lg"
      h="100%"
      data-testid="dashboard-metrics-panel"
    >
      <AgingClaimsCard
        total={metricData?.agingClaims}
        providers={metricData?.agingClaimProviders}
        loading={loading}
        icon={icons.claims}
        navigate={navigate}
        statuses={statuses}
      />
      <ErrorClaimsCard
        total={metricData?.errorClaims}
        providers={metricData?.errorClaimProviders}
        loading={loading}
        icon={icons.error}
        navigate={navigate}
      />
      <RemittanceCard
        remittance={latestRemittance}
        icon={icons.remittances}
        loading={loading}
      />
      <SubmissionCard
        submission={latestSubmission}
        icon={icons.submissions}
        loading={loading}
      />
    </SimpleGrid>
  );
};
